import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { authenticatedUserEmailState } from '~/src/Auth/store';
import {
  queryFilterSelector,
  SearchParams,
  searchStringState,
} from '~/src/FilterProjects/store';
import useApiClient from '~/src/hooks/useApiClient';
import { currentFolderState } from '~/src/ProjectFolder/store';
import { snackbarMessageSelector } from '~/src/Snackbar/store';
import { ENTRIES_PER_PAGE } from '../constants';
import { Entry, pendingLoadMore, projectListState } from '../store';

const useLoadMore = () => {
  const { t } = useTranslation('projects');
  const apiClient = useApiClient();
  const setPending = useSetRecoilState(pendingLoadMore);
  const setProjects = useSetRecoilState(projectListState);
  const filters = useRecoilValue(queryFilterSelector);
  const searchString = useRecoilValue(searchStringState);
  const currentFolder = useRecoilValue(currentFolderState);
  const setSnackbarMessage = useSetRecoilState(snackbarMessageSelector);
  const userEmail = useRecoilValue(authenticatedUserEmailState);
  const isPending = useRecoilValue(pendingLoadMore);

  // Ref
  const currentPage = useRef(0);

  const LOAD_MORE_PER_PAGE = ENTRIES_PER_PAGE;

  const loadMore = async () => {
    if (isPending) {
      return;
    }

    currentPage.current += 1;

    try {
      setPending(true);
      const params: SearchParams = {
        limit: LOAD_MORE_PER_PAGE,
        skip: currentPage.current * ENTRIES_PER_PAGE,
      };

      let requestFilters;

      switch (true) {
        case !!filters && !currentFolder:
          requestFilters = filters;
          break;
        case !filters && !!currentFolder:
          requestFilters = { folderId: currentFolder?._id };
          break;
        case !!filters && !!currentFolder:
          requestFilters = {
            folderId: currentFolder?._id,
            ...(filters || {}),
          };
          break;
        default:
          requestFilters = {};
      }

      params.filter = btoa(JSON.stringify(requestFilters));

      if (searchString?.value) {
        params.query = searchString?.value;
      }

      const {
        data: {
          data,
          pagination,
        },
      } = await apiClient.get<{
        data: Entry[];
        pagination: { hasMore: boolean };
      }>('/search', { params });

      setProjects((state) => {
        const existingIds = new Set(state.entries.map((e: any) => e._id));
        const deduped = (data as any[]).filter((e) => !existingIds.has(e._id));
        return {
          entries: [...state.entries, ...deduped],
          hasMore: pagination?.hasMore || false,
        };
      });
    } catch (err) {
      console.error(err);
      setSnackbarMessage({
        message: t('getProjects.errors.loadMore'),
        severity: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    currentPage.current = 0;
  }, [filters, searchString, currentFolder, userEmail]);

  return [loadMore];
};

export default useLoadMore;
